import React from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@org/clients/school';
import {
  DatePicker,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Button,
  MultiSelectFilter,
} from '@org/ui';
import { RotateCcw } from 'lucide-react';
import { useAnalytics } from './analytics-context';
import {
  COMPARE_MODES,
  GRAINS,
  RANGE_PRESETS,
  STUDENT_TYPES,
  parseDateKey,
  toDateKey,
  type CompareMode,
  type GrainChoice,
  type RangePreset,
  type StudentType,
} from './filters';

/**
 * One row of filters above every report. Every control writes to the URL,
 * so a filtered view can be bookmarked or sent to someone.
 */
export function AnalyticsFilterBar() {
  const { t } = useTranslation('admin');
  const { search, update } = useAnalytics();
  const { data: options } = trpc.analytics.filterOptions.useQuery(undefined, { staleTime: 5 * 60_000 });

  const groups = React.useMemo(() => {
    const all = options?.groups ?? [];
    const levels = search.levelIds ?? [];
    const teachers = search.teacherIds ?? [];
    return all.filter(
      (g) =>
        (levels.length === 0 || (g.levelId !== null && levels.includes(g.levelId))) &&
        (teachers.length === 0 || (g.teacherId !== null && teachers.includes(g.teacherId))),
    );
  }, [options, search.levelIds, search.teacherIds]);

  const hasFilters = Boolean(
    search.levelIds?.length ||
      search.groupIds?.length ||
      search.teacherIds?.length ||
      search.studentTypes?.length ||
      search.includeDisabled,
  );

  /** Narrowing levels/teachers drops group picks that no longer fit. */
  const keepFittingGroups = (levelIds: string[], teacherIds: string[]) =>
    (search.groupIds ?? []).filter((id) => {
      const g = options?.groups.find((x) => x.groupId === id);
      if (!g) return false;
      return (
        (levelIds.length === 0 || (g.levelId !== null && levelIds.includes(g.levelId))) &&
        (teacherIds.length === 0 || (g.teacherId !== null && teacherIds.includes(g.teacherId)))
      );
    });

  const multiProps = {
    clearLabel: t('analytics.filters.clear'),
    emptyLabel: t('analytics.filters.noOptions'),
    triggerClassName: 'h-9 rounded-md px-3 py-0 text-sm font-normal shadow-none',
    badgeClassName: 'bg-[#cf2e2e]',
  };

  const field = (label: string, control: React.ReactNode) => (
    <div className="flex min-w-[140px] flex-col gap-1">
      <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</Label>
      {control}
    </div>
  );

  return (
    <div className="rounded-xl border bg-white/80 p-4">
      <div className="flex flex-wrap items-end gap-3">
        {field(
          t('analytics.filters.range'),
          <Select value={search.range} onValueChange={(v) => update({ range: v as RangePreset })}>
            <SelectTrigger className="h-9 w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_PRESETS.map((p) => (
                <SelectItem key={p} value={p}>
                  {t(`analytics.filters.presets.${p}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>,
        )}

        {search.range === 'custom'
          ? field(
              t('analytics.filters.from'),
              <DatePicker
                className="h-9 w-[150px]"
                value={search.from ? parseDateKey(search.from) : undefined}
                dateFormat="d MMM yyyy"
                onChange={(d) => d && update({ range: 'custom', from: toDateKey(d), to: search.to ?? toDateKey(new Date()) })}
              />,
            )
          : null}
        {search.range === 'custom'
          ? field(
              t('analytics.filters.to'),
              <DatePicker
                className="h-9 w-[150px]"
                value={search.to ? parseDateKey(search.to) : undefined}
                dateFormat="d MMM yyyy"
                onChange={(d) => d && update({ range: 'custom', to: toDateKey(d), from: search.from ?? toDateKey(d) })}
              />,
            )
          : null}

        {field(
          t('analytics.filters.grain'),
          <Select value={search.grain} onValueChange={(v) => update({ grain: v as GrainChoice })}>
            <SelectTrigger className="h-9 w-[130px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">{t('analytics.filters.grains.auto')}</SelectItem>
              {GRAINS.map((g) => (
                <SelectItem key={g} value={g}>
                  {t(`analytics.filters.grains.${g}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>,
        )}

        {field(
          t('analytics.filters.compare'),
          <Select value={search.compare} onValueChange={(v) => update({ compare: v as CompareMode })}>
            <SelectTrigger className="h-9 w-[190px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COMPARE_MODES.map((m) => (
                <SelectItem key={m} value={m}>
                  {t(`analytics.compare.${m}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>,
        )}

        {field(
          t('analytics.filters.level'),
          <MultiSelectFilter
            {...multiProps}
            label={search.levelIds?.length ? t('analytics.filters.level') : t('analytics.filters.allLevels')}
            options={(options?.levels ?? []).map((l) => ({ value: l.levelId, label: l.name }))}
            value={search.levelIds ?? []}
            onChange={(ids) => update({ levelIds: ids, groupIds: keepFittingGroups(ids, search.teacherIds ?? []) })}
          />,
        )}

        {field(
          t('analytics.filters.teacher'),
          <MultiSelectFilter
            {...multiProps}
            label={search.teacherIds?.length ? t('analytics.filters.teacher') : t('analytics.filters.allTeachers')}
            options={(options?.teachers ?? []).map((tc) => ({ value: tc.teacherId, label: tc.name }))}
            value={search.teacherIds ?? []}
            onChange={(ids) => update({ teacherIds: ids, groupIds: keepFittingGroups(search.levelIds ?? [], ids) })}
          />,
        )}

        {field(
          t('analytics.filters.group'),
          <MultiSelectFilter
            {...multiProps}
            label={search.groupIds?.length ? t('analytics.filters.group') : t('analytics.filters.allGroups')}
            options={groups.map((g) => ({ value: g.groupId, label: g.name || g.groupId.slice(0, 8) }))}
            value={search.groupIds ?? []}
            onChange={(ids) => update({ groupIds: ids })}
          />,
        )}

        {field(
          t('analytics.filters.studentType'),
          <MultiSelectFilter
            {...multiProps}
            label={search.studentTypes?.length ? t('analytics.filters.studentType') : t('analytics.filters.allTypes')}
            options={STUDENT_TYPES.map((s) => ({ value: s, label: t(`analytics.studentTypes.${s}`) }))}
            value={search.studentTypes ?? []}
            onChange={(ids) => update({ studentTypes: ids as StudentType[] })}
          />,
        )}

        <div className="flex h-9 items-center gap-2">
          <Switch
            checked={Boolean(search.includeDisabled)}
            onCheckedChange={(checked) => update({ includeDisabled: checked || undefined })}
            id="analytics-include-disabled"
          />
          <Label htmlFor="analytics-include-disabled" className="text-xs">
            {t('analytics.filters.includeDisabled')}
          </Label>
        </div>

        {hasFilters ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-9 text-xs"
            onClick={() =>
              update({
                levelIds: undefined,
                groupIds: undefined,
                teacherIds: undefined,
                studentTypes: undefined,
                includeDisabled: undefined,
              })
            }
          >
            <RotateCcw className="mr-1 h-3.5 w-3.5" />
            {t('analytics.filters.reset')}
          </Button>
        ) : null}
      </div>
      <p className="mt-3 text-[11px] text-muted-foreground">{t('analytics.filters.scopeNote')}</p>
    </div>
  );
}
